import React, { useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../lib/api';
import './ExpenseForm.css';

function ExpenseForm({ expense = null, onSuccess, onCancel }) {
  const isEdit = expense !== null;

  const [formData, setFormData] = useState({
    name: expense?.name || '',
    category: expense?.category || 'Subscription',
    amount: expense?.amount || '',
    currency: expense?.currency || 'EUR',
    frequency: expense?.frequency || 'monthly',
    startDate: expense?.startDate || new Date().toISOString().split('T')[0],
    endDate: expense?.endDate || '',
    autoRenew: expense?.autoRenew !== undefined ? expense.autoRenew : true,
    notes: expense?.notes || '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const CATEGORIES = [
    'Subscription',
    'Bill',
    'Insurance',
    'Maintenance',
    'Utilities',
    'Rent/Mortgage',
    'Transportation',
    'Healthcare',
    'Education',
    'Entertainment',
    'Other'
  ];

  const FREQUENCIES = [
    { value: 'monthly', label: 'Monthly' },
    { value: 'yearly', label: 'Yearly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'one-time', label: 'One-time' }
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Validate required fields
      if (!formData.name || !formData.category || !formData.amount || !formData.frequency) {
        throw new Error('Please fill in all required fields');
      }

      // Validate amount
      if (isNaN(parseFloat(formData.amount)) || parseFloat(formData.amount) <= 0) {
        throw new Error('Amount must be a positive number');
      }

      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
        endDate: formData.endDate || null,
      };

      if (isEdit) {
        await axios.put(`${API_BASE_URL}/expenses/${expense.expenseId}`, payload, {
          withCredentials: true
        });
      } else {
        await axios.post(`${API_BASE_URL}/expenses`, payload, {
          withCredentials: true
        });
      }

      onSuccess();
    } catch (err) {
      console.error('Error saving expense:', err);
      setError(err.response?.data?.error || err.message || 'Failed to save expense');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-content expense-form-modal" onClick={(e) => e.stopPropagation()}>
        <h2>{isEdit ? 'Edit Expense' : 'Add New Expense'}</h2>

        <form onSubmit={handleSubmit}>
          {error && <div className="error-message">{error}</div>}

          <div className="form-row">
            <div className="form-group full-width">
              <label htmlFor="name">
                Expense Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Netflix, Car Insurance"
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="category">
                Category <span className="required">*</span>
              </label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
              >
                {CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="frequency">
                Frequency <span className="required">*</span>
              </label>
              <select
                id="frequency"
                name="frequency"
                value={formData.frequency}
                onChange={handleChange}
                required
              >
                {FREQUENCIES.map(freq => (
                  <option key={freq.value} value={freq.value}>{freq.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="amount">
                Amount <span className="required">*</span>
              </label>
              <input
                type="number"
                id="amount"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                step="0.01"
                min="0"
                placeholder="0.00"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="currency">Currency</label>
              <select
                id="currency"
                name="currency"
                value={formData.currency}
                onChange={handleChange}
              >
                <option value="EUR">EUR (€)</option>
                <option value="USD">USD ($)</option>
                <option value="GBP">GBP (£)</option>
                <option value="PLN">PLN (zł)</option>
                <option value="CZK">CZK (Kč)</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="startDate">Start Date</label>
              <input
                type="date"
                id="startDate"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="endDate">End Date (optional)</label>
              <input
                type="date"
                id="endDate"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
              />
            </div>
          </div>

          {formData.frequency !== 'one-time' && (
            <div className="form-row">
              <div className="form-group full-width">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="autoRenew"
                    checked={formData.autoRenew}
                    onChange={handleChange}
                  />
                  <span>Auto-renewing subscription/bill</span>
                </label>
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group full-width">
              <label htmlFor="notes">Notes (optional)</label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows="3"
                placeholder="Additional details..."
              />
            </div>
          </div>

          <div className="form-actions">
            <button type="button" onClick={onCancel} disabled={loading}>
              Cancel
            </button>
            <button type="submit" disabled={loading} className="primary">
              {loading ? 'Saving...' : isEdit ? 'Update Expense' : 'Add Expense'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ExpenseForm;
